/**
 * Vault: 데이터 키(DEK)의 생성·포장·해제 (계획서 §6, SYNC_SECURITY §5).
 *
 *   passphrase ─Argon2id─► KEK_p ─┐
 *   recovery key ─HKDF───► KEK_r ─┼─ AES-KW ─► wrappedDek_p / wrappedDek_r  (헤더에 저장)
 *                                 └─ DEK (AES-GCM-256, 사용 시 non-extractable)
 *
 * 헤더에는 평문 상담데이터가 없다. passphrase·DEK 원문은 어디에도 저장하지 않는다.
 */
import { uuidv7 } from 'uuidv7';
import { fromBase64, randomBytes, toBase64, utf8 } from './encoding';
import type { DataKey } from './envelope';
import { assertProductionParams, deriveKeyBytes, type KdfParams } from './kdf';
import { generateRecoveryKey, parseRecoveryKey } from './recoveryKey';

export type VaultHeader = {
  format: 'counselai-vault';
  formatVersion: 1;
  vaultId: string;
  kid: string;
  createdAt: string;
  /** passphrase 변경 등 헤더가 바뀐 시각 (동기화 시 최신 헤더 채택) */
  updatedAt?: string;
  kdf: KdfParams & { salt: string };
  wrappedDekPassphrase: string;
  recovery: { salt: string; wrappedDek: string };
};

export class WrongSecretError extends Error {
  constructor() {
    super('passphrase 또는 복구 키가 맞지 않습니다');
    this.name = 'WrongSecretError';
  }
}

async function kekFromBytes(bytes: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', bytes as BufferSource, { name: 'AES-KW' }, false, ['wrapKey', 'unwrapKey']);
}

async function passphraseKek(passphrase: string, header: Pick<VaultHeader, 'kdf'>): Promise<CryptoKey> {
  const { salt, ...params } = header.kdf;
  return kekFromBytes(await deriveKeyBytes(passphrase, fromBase64(salt), params));
}

async function recoveryKek(recoveryBytes: Uint8Array, salt: Uint8Array): Promise<CryptoKey> {
  const ikm = await crypto.subtle.importKey('raw', recoveryBytes as BufferSource, 'HKDF', false, ['deriveBits']);
  const bits = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: salt as BufferSource,
      info: utf8.encode('counselai-recovery-kek-v1') as BufferSource,
    },
    ikm,
    256,
  );
  return kekFromBytes(new Uint8Array(bits));
}

async function unwrap(wrapped: string, kek: CryptoKey, extractable: boolean): Promise<CryptoKey> {
  try {
    return await crypto.subtle.unwrapKey(
      'raw',
      fromBase64(wrapped) as BufferSource,
      kek,
      'AES-KW',
      { name: 'AES-GCM', length: 256 },
      extractable,
      ['encrypt', 'decrypt'],
    );
  } catch {
    throw new WrongSecretError();
  }
}

async function wrap(dek: CryptoKey, kek: CryptoKey): Promise<string> {
  return toBase64(new Uint8Array(await crypto.subtle.wrapKey('raw', dek, kek, 'AES-KW')));
}

export type CreatedVault = { header: VaultHeader; dataKey: DataKey; recoveryKeyDisplay: string };

export async function createVault(
  passphrase: string,
  kdf: KdfParams,
  opts: { allowWeakKdfForTests?: boolean; now?: Date } = {},
): Promise<CreatedVault> {
  if (!opts.allowWeakKdfForTests) assertProductionParams(kdf);
  const dekExtractable = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, [
    'encrypt',
    'decrypt',
  ]);
  const salt = randomBytes(16);
  const header0 = { kdf: { ...kdf, salt: toBase64(salt) } };
  const kekP = await passphraseKek(passphrase, header0);
  const recovery = generateRecoveryKey();
  const recoverySalt = randomBytes(16);
  const kekR = await recoveryKek(recovery.bytes, recoverySalt);

  const header: VaultHeader = {
    format: 'counselai-vault',
    formatVersion: 1,
    vaultId: uuidv7(),
    kid: uuidv7(),
    createdAt: (opts.now ?? new Date()).toISOString(),
    kdf: header0.kdf,
    wrappedDekPassphrase: await wrap(dekExtractable, kekP),
    recovery: { salt: toBase64(recoverySalt), wrappedDek: await wrap(dekExtractable, kekR) },
  };
  // 사용 키는 non-extractable로 다시 해제
  const key = await unwrap(header.wrappedDekPassphrase, kekP, false);
  return { header, dataKey: { key, kid: header.kid }, recoveryKeyDisplay: recovery.display };
}

export async function unlockWithPassphrase(header: VaultHeader, passphrase: string): Promise<DataKey> {
  const key = await unwrap(header.wrappedDekPassphrase, await passphraseKek(passphrase, header), false);
  return { key, kid: header.kid };
}

/** passphrase 변경: DEK는 그대로, 포장만 교체 (데이터 재암호화 없음) */
export async function changePassphrase(
  header: VaultHeader,
  oldPassphrase: string,
  newPassphrase: string,
  kdf: KdfParams = header.kdf,
  opts: { allowWeakKdfForTests?: boolean } = {},
): Promise<VaultHeader> {
  const dek = await unwrap(header.wrappedDekPassphrase, await passphraseKek(oldPassphrase, header), true);
  return rewrapForPassphrase(header, dek, newPassphrase, kdf, opts);
}

/** 복구 키로 열고 새 passphrase 설정 */
export async function recoverWithKey(
  header: VaultHeader,
  recoveryKeyInput: string,
  newPassphrase: string,
  opts: { allowWeakKdfForTests?: boolean } = {},
): Promise<{ header: VaultHeader; dataKey: DataKey }> {
  let bytes: Uint8Array;
  try {
    bytes = parseRecoveryKey(recoveryKeyInput);
  } catch {
    throw new WrongSecretError();
  }
  const kekR = await recoveryKek(bytes, fromBase64(header.recovery.salt));
  const dek = await unwrap(header.recovery.wrappedDek, kekR, true);
  const next = await rewrapForPassphrase(header, dek, newPassphrase, header.kdf, opts);
  return { header: next, dataKey: await unlockWithPassphrase(next, newPassphrase) };
}

async function rewrapForPassphrase(
  header: VaultHeader,
  dek: CryptoKey,
  newPassphrase: string,
  kdf: KdfParams,
  opts: { allowWeakKdfForTests?: boolean },
): Promise<VaultHeader> {
  const params: KdfParams = {
    alg: kdf.alg,
    memoryKiB: kdf.memoryKiB,
    iterations: kdf.iterations,
    parallelism: kdf.parallelism,
  };
  if (!opts.allowWeakKdfForTests) assertProductionParams(params);
  const salt = toBase64(randomBytes(16));
  const nextKdf = { ...params, salt };
  const kekP = await passphraseKek(newPassphrase, { kdf: nextKdf });
  return { ...header, kdf: nextKdf, wrappedDekPassphrase: await wrap(dek, kekP), updatedAt: new Date().toISOString() };
}

export function isVaultHeader(v: unknown): v is VaultHeader {
  const h = v as VaultHeader | null;
  return !!h && h.format === 'counselai-vault' && h.formatVersion === 1 && typeof h.wrappedDekPassphrase === 'string';
}
